import type { AnalyticsEventEnumType } from '@forgd/contract/types'
import posthog from 'posthog-js'

type AnalyticsUserType = 'anonymous' | 'project' | 'advisor' | 'market_maker'
type AnalyticsContext = Record<string, string | boolean | undefined>

export function useAnalytics() {
  const auth = useAuth()
  const runtimeConfig = useRuntimeConfig()

  function resolveAnalyticsContext(): AnalyticsContext {
    const isLoggedIn = Boolean(auth.isLoggedIn)

    let userType: AnalyticsUserType = 'anonymous'
    if (isLoggedIn) {
      if (auth.isMarketMaker) {
        userType = 'market_maker'
      }
      else if (auth.isAdvisor) {
        userType = 'advisor'
      }
      else {
        userType = 'project'
      }
    }

    const portalValue = runtimeConfig.public.portal

    return {
      is_logged_in: isLoggedIn,
      user_type: userType,
      portal: typeof portalValue === 'string' ? portalValue : undefined,
      project_id: auth.project?.id,
      user_id: isLoggedIn ? auth.me?.id : undefined,
    }
  }

  function trackPosthog(event: AnalyticsEventEnumType, properties: Record<string, unknown>) {
    try {
      posthog.capture(event, properties)
    }
    catch {}
  }

  function applyZarazContext(context: AnalyticsContext) {
    if (typeof window === 'undefined' || typeof window.zaraz?.set !== 'function') {
      return
    }

    Object.entries(context).forEach(([key, value]) => {
      window.zaraz?.set(key, value, { scope: 'session' })
    })
  }

  function trackZaraz(
    event: AnalyticsEventEnumType,
    context: AnalyticsContext,
    properties: Record<string, unknown>,
  ) {
    if (typeof window === 'undefined' || typeof window.zaraz?.track !== 'function') {
      return
    }

    try {
      applyZarazContext(context)
      void Promise.resolve(window.zaraz.track(event, properties)).catch(() => {})
    }
    catch {}
  }

  function capture(event: AnalyticsEventEnumType, properties?: Record<string, unknown>) {
    const context = resolveAnalyticsContext()
    const merged = {
      ...properties,
      ...context,
      project_id: properties?.project_id ?? context.project_id,
    }

    trackPosthog(event, merged)
    trackZaraz(event, context, merged)
  }

  return { capture }
}
